import {setUserFromAuthenticationResponse} from 'monitoring/sentry.ts';
import {UserContext, UserContextType} from 'pages/auth/UserContext.ts';
import {useContext, useEffect, useRef} from 'react';
import {
  UserStateCombined,
  UserStateFailed,
  UserStateLoaded,
} from 'types/userState/userState';
import {SetTokenType, useToken} from 'utils/hooks/useTokenHook.ts';
import {AccessStatus} from 'utils/userTypes.ts';
import {getCemitAuthApiUrl} from '../../apis/cemitApis/apiConfig.ts';
import {clsOrType} from '../../appUtils/typeUtils/clsOrType.ts';
import {Access, AccessResponse} from '../../types/authentication/access';
import {CemitTypename} from '../../types/cemitTypename.ts';
import {StateSetter} from '../../types/hookHelpers/stateSetter';
import {head} from 'ramda';
import {
  getStoredTokenOrUndefined,
  makeEmptyToken,
  mergeContextLog,
  TokenPresentType,
  TokenType,
} from 'appUtils/authentication/authenticationUtils.ts';
import {ACCESS_LEVELS} from 'pages/trafficsim/utils/access_levels.ts';
import {resolvePartnerOrganizationSourceKeyIfDefined} from 'utils/organization/organizationUtils.ts';
import {authApiNowMs, followStoredToken, useTokenRefresh} from './tokenRefreshHooks.ts';
import {
  checkjwtRefusal,
  JWT_EXPIRED_MESSAGE,
  needsTokenExchange,
  extractTokenClaims,
  checkForRenewedProfile,
  TokenRefreshResult,
} from '../../appUtils/authentication/tokenRefreshUtils.ts';

/**
 * Authenticates the user based on the available token and returns the userState and setUserState
 */
export const useAuthenticate = () => {
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('useAuthenticate: start');
  }

  const {userState, setUserState} = useContext(UserContext) as UserContextType;
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('useAuthenticate: useToken()');
  }
  const [token, setToken] = useToken();
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('useAuthenticate: useToken() -> current token', {
      hasToken: !!token.raw,
      context: token.context,
      contextLog: token.contextLog,
    });
  }
  // Renews the token while the app is open; undefined when refreshing is off
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('useAuthenticate: useTokenRefresh()');
  }
  const refresher = useTokenRefresh(token, setToken);
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('useAuthenticate: useTokenRefresh()', {hasRefresher: !!refresher});
  }

  // The token the current user state was authenticated from
  const authenticatedTokenRef = useRef<TokenType>({
    raw: undefined,
    context: 'unknown',
    contextLog: [],
  });
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('useAuthenticate: authenticated token', {
      raw: authenticatedTokenRef.current.raw,
      context: authenticatedTokenRef.current.context,
      contextLog: authenticatedTokenRef.current.contextLog,
    });
  }

  useEffect(() => {
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('useAuthenticate.useEffect: start');
    }
    // A renewed token of the session that is logged in needs no new login check: the
    // auth API applied the login rules when it renewed it. The user state is updated from
    // its claims, the user's current profile, if they changed.
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('useAuthenticate.useEffect.checkForRenewedProfile', {
        currentRaw: authenticatedTokenRef.current.raw,
        currentContext: authenticatedTokenRef.current.context,
        newRaw: token.raw,
        newContext: token.context,
      });
    }
    if (authenticatedTokenRef.current.raw !== undefined) {
      if (authenticatedTokenRef.current.raw !== token.raw) {
        if (process.env.REACT_AUTH_DEBUG && console.log) {
          console.log('useAuthenticate.useEffect: checkForRenewedProfile()', {
            hasAuthToken: authenticatedTokenRef.current.raw !== undefined,
            isSameToken: authenticatedTokenRef.current.raw === token.raw,
          });
        }
        const renewal = checkForRenewedProfile(
          authenticatedTokenRef.current.raw,
          token.raw,
          userState?.status === AccessStatus.Authenticated,
        );
        if (renewal) {
          if (process.env.REACT_AUTH_DEBUG && console.log) {
            console.log(
              'useAuthenticate.useEffect: set authenticatedTokenRef.current=token',
            );
          }
          // Update authenticated reference if token is new, otherwise try to
          // avoid updates to avoid triggering react.
          if (
            token.raw &&
            !(
              authenticatedTokenRef.current.raw === token.raw &&
              authenticatedTokenRef.current.context === token.context
            )
          ) {
            authenticatedTokenRef.current = {
              raw: token.raw,
              context: token.context,
              contextLog: token.contextLog,
            };
            if (process.env.REACT_AUTH_DEBUG && console.log) {
              console.log(
                'useAuthenticate.useEffect: set authenticatedTokenRef.current',
                {
                  context: authenticatedTokenRef.current.context,
                  contextLog: authenticatedTokenRef.current.contextLog,
                },
              );
            }
          }
          if (renewal.changed) {
            if (process.env.REACT_AUTH_DEBUG && console.log) {
              console.log('useAuthenticate.useEffect: profile renewed, setUserState()');
            }
            setUserState((previous: UserStateCombined) =>
              getUserStateFromProfile(renewal.profile as AccessResponse['message']),
            );
          }
          if (process.env.REACT_AUTH_DEBUG && console.log) {
            console.log('useAuthenticate.useEffect: end (renewal)');
          }
          return;
        }
        if (process.env.REACT_AUTH_DEBUG && console.log) {
          console.log(
            'useAuthenticate.useEffect: set authenticatedTokenRef.current=undefined',
          );
        }
        authenticatedTokenRef.current = makeEmptyToken();
      } else {
        if (process.env.REACT_AUTH_DEBUG && console.log) {
          console.log(
            'useAuthenticate.useEffect.checkForRenewedProfile authenticated token is the same as useToken',
            {
              context: authenticatedTokenRef.current.context,
              contextLog: authenticatedTokenRef.current.contextLog,
            },
          );
        }
        // If the authenticated token has a raw value it means it was verified
        // by checkjwt or refreshjwt by a previous call of useAuthenticate or
        // one of the useEffect/callbacks. There is no need to re-run
        // authenticate/requestTokenRefresh.
        if (authenticatedTokenRef.current.raw !== undefined) {
          if (process.env.REACT_AUTH_DEBUG && console.log) {
            console.log(
              'useAuthenticate.useEffect.checkForRenewedProfile authenticated token is still valid, no need to re-check, end',
            );
          }
          return;
        }
      }
    } else {
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log(
          'useAuthenticate.useEffect.checkForRenewedProfile no authenticated token',
        );
      }
    }

    // Ignores the answer for a token that has been replaced in the meantime
    let isCurrent = true;
    // Run authentication process by contacting API, once it is done it will update the user state
    if (token.raw) {
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('useAuthenticate.useEffect: has token', {hasRefresher: !!refresher});
      }
      const authenticateToken = () => {
        if (process.env.REACT_AUTH_DEBUG && console.log) {
          console.log('useAuthenticate.useEffect.authenticateToken: authenticate()', {
            context: token.context,
            contextLog: token.contextLog,
          });
        }
        return authenticate({
          token,
          isCurrent: () => isCurrent,
          onAuthenticated: (authProfile: AccessResponse['message']) => {
            if (process.env.REACT_AUTH_DEBUG && console.log) {
              console.log('useAuthenticate.useEffect: onAuthenticated');
            }
            const userState = getUserStateFromProfile(authProfile);
            if (userState.status === AccessStatus.Authenticated) {
              if (process.env.REACT_AUTH_DEBUG && console.log) {
                console.log(
                  'useAuthenticate.useEffect: onAuthenticated(profile-ok): setUserState() from profile',
                );
              }
              setUserState(userState);
              if (process.env.REACT_AUTH_DEBUG && console.log) {
                console.log(
                  'useAuthenticate.useEffect: onAuthenticated(profile-ok): set authenticatedTokenRef.current=authenticated token',
                );
              }
              authenticatedTokenRef.current = {
                raw: token.raw,
                context: 'checkjwt',
                contextLog: mergeContextLog(token.contextLog, 'checkjwt'),
              };
            } else {
              if (process.env.REACT_AUTH_DEBUG && console.log) {
                console.log(
                  'useAuthenticate.useEffect: onAuthenticated(profile-failed): setUserState(invalid)',
                );
              }
              setUserState(userState);
              if (process.env.REACT_AUTH_DEBUG && console.log) {
                console.log(
                  'useAuthenticate.useEffect: onAuthenticated(profile-failed): set authenticatedTokenRef.current=authenticated token',
                );
              }
              authenticatedTokenRef.current = {
                raw: undefined,
                context: 'checkjwt',
                contextLog: mergeContextLog(token.contextLog, 'checkjwt'),
              };
            }
          },
          onInvalidUser: () => {
            if (process.env.REACT_AUTH_DEBUG && console.log) {
              console.log(
                'useAuthenticate.useEffect: onInvalidUser: setInvalidUserState',
              );
            }
            setInvalidUser({setUserState, token, setToken, context: 'checkjwt'});
          },
          // The token expired before this tab planned, e.g. on a wrong clock; the auth API
          // still renews it for a grace period. The scheduler refreshes it now, so it
          // retries if the auth API cannot be reached, and the exchange shares its request.
          onExpired: refresher
            ? () => {
                if (process.env.REACT_AUTH_DEBUG && console.log) {
                  console.log('useAuthenticate.useEffect.onExpired: refreshing', {
                    token,
                    isCurrent,
                  });
                }
                refresher.refreshNow();
                requestTokenRefresh({
                  token,
                  isCurrent: () => isCurrent,
                  requestRefresh: refresher.requestRefresh,
                  onRefused: () => {
                    if (process.env.REACT_AUTH_DEBUG && console.log) {
                      console.log(
                        'useAuthenticate.useEffect.authenticate().onExpired.refresh.onRefused: setInvalidUser()',
                      );
                    }
                    setInvalidUser({
                      setUserState,
                      token,
                      setToken,
                      context: 'refreshjwt',
                    });
                  },
                  onUnavailable: (reason: string) => {
                    if (console.error) {
                      console.error('Failed renewing JWT: ' + reason);
                    }
                    setUserState(
                      clsOrType<UserStateFailed>(CemitTypename.userStateFailed, {
                        status: AccessStatus.Invalid,
                        error: 'Authentication unavailable',
                      }),
                    );
                  },
                });
              }
            : undefined,
          onServiceOutage: () => {
            if (process.env.REACT_AUTH_DEBUG && console.log) {
              console.log(
                'useAuthenticate.useEffect: onServiceOutage: setUserState invalid',
              );
            }
            setUserState(
              clsOrType<UserStateFailed>(CemitTypename.userStateFailed, {
                status: AccessStatus.Invalid,
                error: 'Authentication error',
              }),
            );
          },
        });
      };

      const claims = refresher ? extractTokenClaims(token.raw) : undefined;
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('useAuthenticate.useEffect: claims:', {claims});
      }
      if (refresher && claims && needsTokenExchange(claims, authApiNowMs())) {
        if (process.env.REACT_AUTH_DEBUG && console.log) {
          console.log('useAuthenticate: needs token exchange: requestTokenRefresh()', {
            exp: claims.exp,
          });
        }
        requestTokenRefresh({
          token,
          isCurrent: () => isCurrent,
          requestRefresh: refresher.requestRefresh,
          onRefused: () => {
            if (process.env.REACT_AUTH_DEBUG && console.log) {
              console.log(
                'useAuthenticate.useEffect.requestTokenRefresh.onRefused setInvalidUser()',
              );
            }
            setInvalidUser({setUserState, token, setToken, context: 'refreshjwt'});
          },
          // A login link token is still accepted by checkjwt
          onUnavailable: () => {
            if (claims.exp === undefined) {
              authenticateToken();
            }
          },
        });
      } else {
        if (process.env.REACT_AUTH_DEBUG && console.log) {
          console.log('useAuthenticate.useEffect: authenticateToken()');
        }
        authenticateToken();
      }
    } else {
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('useAuthenticate.useEffect: no token, setUnauthenticated');
      }
      // No token so cannot authenticate, change state of authentication to failed (ie. unathenticated)
      if (
        !(
          userState.status == AccessStatus.Invalid &&
          userState.sourceKey == '' &&
          userState.sourceKey == ''
        )
      ) {
        setUserState({
          status: AccessStatus.Invalid,
          sourceKey: '',
          error: '',
        } as UserStateFailed);
      }
    }
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('useAuthenticate.useEffect: end (normal)');
    }
    return () => {
      isCurrent = false;
    };
  }, [token]);
  // useEffect: end

  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('useAuthenticate return');
  }
  return {userState, setUserState};
};

const loadLocalAuthData = (): AccessResponse => {
  if (process.env.REACT_DISABLE_AUTH_CHECK === 'true') {
    let json: AccessResponse;
    if (
      process.env.REACT_APP_FORCED_JWT !== undefined &&
      process.env.REACT_APP_FORCED_JWT != ''
    ) {
      try {
        json = JSON.parse(process.env.REACT_APP_FORCED_JWT);
        console.log('Authenticated from env variable', json);
      } catch (error) {
        console.log('Failed to authenticate from env variable', error);
        json = {
          status: '',
          ok: true,
          message: {},
        };
      }
    } else {
      console.log('Authenticated with hardcoded values (sysadmin)');
      json = {
        status: '',
        ok: true,
        message: {
          id: 'admin',
          email: 'admin@example.com',
          group: 'sysadmin',
          customerId: 'kiwi_rail',
        },
      };
    }
    return json;
  } else {
    throw new Error('loadLocalAuthData() was called when auth check is enabled');
  }
};

/**
 * The user state for a user profile: the claims of a token, as returned by checkjwt or
 * carried by a renewed token
 * @param message The profile
 */
const getUserStateFromProfile = (
  message: AccessResponse['message'],
): UserStateLoaded | UserStateFailed => {
  const email = message?.email;
  const group = message?.group ?? '';
  const trackAccess = Array.isArray(message?.trackAccess) ? message?.trackAccess : [];
  const applicationAccess = Array.isArray(message?.tabAccess) ? message?.tabAccess : [];
  const status =
    email && (group === 'sysadmin' || applicationAccess)
      ? AccessStatus.Authenticated
      : AccessStatus.Invalid;

  // Resolves to one ACCESS_LEVELS, used only for trafficSim
  const trafficSimAccessLevel =
    group === 'sysadmin'
      ? ACCESS_LEVELS.ADMIN
      : head(
          Array.from(applicationAccess).filter((s) => s.substring(0, 9) === 'tracksim_'),
        );

  const customerId =
    message?.customerId &&
    // Convert partner organization sourceKey to the end-user organization
    resolvePartnerOrganizationSourceKeyIfDefined(message?.customerId);

  return status === AccessStatus.Authenticated
    ? clsOrType<UserStateLoaded>(CemitTypename.userStateLoaded, {
        // Must either be a syadmin or have at least one product available to be considered valid
        status: AccessStatus.Authenticated,
        id: message?.id ?? '',
        sourceKey: customerId,
        email: email ?? '',
        access: clsOrType<Access>(CemitTypename.access, {
          trackAccess: trackAccess,
          applicationAccess: applicationAccess,
          group: group,
          // TODO temp read/write level for trafficsim
          trafficSimAccessLevel,
        }),
      })
    : clsOrType<UserStateFailed>(CemitTypename.userStateFailed, {
        // Must either be a syadmin or have at least one product available to be considered valid
        status: AccessStatus.Invalid,
        sourceKey: customerId,
      });
};

/**
 * Authenticates the user from the token and sets a new user state depending
 * on whether it failed or was authenticated.
 * @param setUserState Function to set new user state
 * @param token String containing the JWT token
 * @param isCurrent Whether the token is still the current one; if not, the answer is ignored
 * @param onAuthenticated Called when the user state has been set from this token
 * @param onExpired Called instead of logging out when checkjwt refuses the token because
 * it has expired, if it can be renewed
 * @return
 */
const authenticate = async ({
  token,
  isCurrent,
  onAuthenticated,
  onInvalidUser,
  onServiceOutage,
  onExpired,
}: {
  token: TokenPresentType;
  isCurrent: () => boolean;
  onAuthenticated: (authProfile: AccessResponse['message']) => void;
  onInvalidUser: () => void;
  onServiceOutage: () => void;
  onExpired?: () => void;
}) => {
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('authenticate start');
  }
  try {
    let json: AccessResponse;
    if (process.env.REACT_DISABLE_AUTH_CHECK !== 'true') {
      const url = `${getCemitAuthApiUrl()}/checkjwt`;
      const response: Response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({token: token.raw}),
      });
      if (!isCurrent()) {
        return;
      }
      const responseStatus = response.status;
      if (!response.ok) {
        const refusal =
          responseStatus === 401 || responseStatus === 403
            ? await response.json().then(checkjwtRefusal, () => undefined)
            : undefined;
        if (!isCurrent()) {
          return;
        }
        if (refusal === JWT_EXPIRED_MESSAGE && onExpired) {
          onExpired();
        } else if (refusal !== undefined) {
          // User could not be authenticated using JWT token
          onInvalidUser();
        } else {
          // Generic HTTP response status, e.g. the auth API failing or a gateway page. The
          // token is kept, since only a login link could replace it; reloading checks it again
          if (console.error) {
            console.error('Failed checking JWT, HTTP response: ' + response.statusText);
          }
          onServiceOutage();
        }
        return;
      }
      json = await response.json();
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('authenticate: checkjwt response');
      }
      if (!isCurrent()) {
        return;
      }
      // Log in Sentry
      setUserFromAuthenticationResponse(json);
    } else {
      // This is the code path that can be used for local development
      json = loadLocalAuthData();
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('authenticate: local hardcoded response');
      }
    }

    onAuthenticated(json?.message);

    // environmentAccess isn't set on the user. Ignore this for now
    // if (
    //   json.message.environmentAccess ||
    //   (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'local')
    // ) {
    //   if (
    //     !(
    //       json.message.environmentAccess?.includes(process.env.NODE_ENV) ||
    //       json.message.group === 'sysadmin'
    //     )
    //   ) {
    //     appProps.setToken(undefined)
    //     navigate('/');
    //     return;
    //   }
    // }
  } catch (error) {
    if (!isCurrent()) {
      return;
    }
    // Generic error, for instance network problems
    if (console.error) {
      console.error(`Failed checking JWT: ${error}`);
    }

    onServiceOutage();
    return;
  }
};

/**
 * Exchanges a login link token or an expired token for a new one before the login
 * check. Storing the new token runs the login check for it, so it is checked once. Only
 * the auth API's refresh endpoint accepts an expired token.
 * @param requestRefresh Refreshes and stores a token, see useTokenRefresh
 * @param onUnavailable What to do when the auth API cannot renew the token. Without it,
 * the user is shown that the authentication service could not be reached, and the token
 * is kept, so the refresh can be retried
 */
const requestTokenRefresh = async ({
  token,
  isCurrent,
  requestRefresh,
  onRefused,
  onUnavailable,
}: {
  token: TokenPresentType;
  isCurrent: () => boolean;
  requestRefresh: (token: string) => Promise<TokenRefreshResult>;
  onRefused: () => void;
  onUnavailable?: (reason: string) => void;
}) => {
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('requestTokenRefresh.requestRefresh request');
  }
  const result: TokenRefreshResult = await requestRefresh(token.raw).catch((error) => ({
    kind: 'unavailable',
    reason: `${error?.message ?? error}`,
  }));
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('requestTokenRefresh.requestRefresh result', {kind: result.kind});
  }
  if (!isCurrent()) {
    return;
  }
  if (result.kind === 'refused') {
    onRefused();
  } else if (result.kind === 'unavailable') {
    if (onUnavailable) {
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('requestTokenRefresh.requestRefresh onUnavailable');
      }
      onUnavailable(result.reason);
    }
  }
  // Otherwise the stored token has changed, and its login check follows
};

/**
 * Shows that the user could not be authenticated and clears the refused token, unless
 * another tab has stored a new token meanwhile: this tab then follows it instead
 */
const setInvalidUser = ({
  setUserState,
  token,
  setToken,
  context,
}: {
  setUserState: StateSetter<UserStateCombined>;
  token: TokenType;
  setToken: SetTokenType;
  context: string;
}) => {
  const storedToken = getStoredTokenOrUndefined('setInvalidUser');
  if (
    process.env.REACT_APP_FORCED_TOKEN === undefined &&
    storedToken.raw &&
    storedToken.raw !== token.raw
  ) {
    followStoredToken();
    return;
  }

  setUserState(
    clsOrType<UserStateFailed>(CemitTypename.userStateFailed, {
      status: AccessStatus.Invalid,
      error: 'Invalid user',
    }),
  );
  if (process.env.REACT_AUTH_DEBUG && console.log) {
    console.log('setInvalidUser -> setToken()');
  }
  setToken((prev) => {
    return {
      raw: undefined,
      context,
      contextLog: mergeContextLog(prev.contextLog, context),
    };
  });
};
